import { isOutboundTestMode } from '../lib/stagingEnvironment'
import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'

const stripePromise = isOutboundTestMode()
  ? null
  : loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)

const PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID

const CARD_STYLE = {
  style: {
    base: {
      color: '#f2f2f2',
      fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
      fontSize: '14px',
      '::placeholder': { color: '#888' }
    },
    invalid: { color: '#e57373' }
  },
  // Stripe's built-in postal code field validates against US zip format by
  // default, which rejects valid UK postcodes (e.g. CM6 1BH). Disabling it
  // here and collecting postcode as a normal text field instead.
  hidePostalCode: true
}

function CheckoutForm() {
  const stripe = useStripe()
  const elements = useElements()
  const { user, workshop, workshopId, refreshWorkshop, signOut } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [postcode, setPostcode] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    if (!stripe || !elements) return
    if (!postcode.trim()) { setError('Postcode required.'); return }
    setLoading(true)
    setError(null)

    try {
      const card = elements.getElement(CardElement)
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: {
          email: user.email,
          name: workshop?.name,
          address: { postal_code: postcode.trim().toUpperCase(), country: 'GB' }
        }
      })

      if (pmError) { setError(pmError.message); setLoading(false); return }

      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before starting your subscription.')

      const startSubscription = async (confirmedSetupIntentId = '') => {
        const res = await fetch('/.netlify/functions/create-subscription', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            workshopId,
            paymentMethodId: paymentMethod.id,
            priceId: PRICE_ID,
            confirmedSetupIntentId,
          })
        })
        return res.json()
      }

      let data = await startSubscription()
      if (data.error) { setError(data.error); setLoading(false); return }

      if (data.requiresSetupConfirmation) {
        if (!data.setupIntentClientSecret) {
          setError('Stripe needs card authentication but did not return a setup secret. Please retry or contact support.')
          setLoading(false)
          return
        }
        const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(data.setupIntentClientSecret)
        if (confirmError) { setError(confirmError.message); setLoading(false); return }
        data = await startSubscription(setupIntent?.id || data.setupIntentId)
        if (data.error) { setError(data.error); setLoading(false); return }
      }

      if (data.requiresSetupConfirmation) {
        setError('Card setup still needs authentication. Please retry or contact support if this persists.')
        setLoading(false)
        return
      }

      if (data.status !== 'trialing') {
        setError(`Subscription status: ${data.status}. Please contact support if this persists.`)
        setLoading(false)
        return
      }

      await refreshWorkshop()
      navigate('/')
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="field">
        <label>Billing Postcode</label>
        <input
          value={postcode}
          onChange={e => setPostcode(e.target.value)}
          placeholder="e.g. CM6 1BH"
          required
          style={{ marginBottom: 16 }}
        />
      </div>

      <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 5, padding: '12px 14px', marginBottom: 20 }}>
        <CardElement options={CARD_STYLE} />
      </div>

      <button className="btn btn-primary btn-full" type="submit" disabled={!stripe || loading}>
        {loading ? <><span className="spinner" />&nbsp;Processing…</> : 'Start 90-day free trial'}
      </button>

      <div className="legal-footer" style={{ marginTop: 14 }}>
        By subscribing you agree to our{' '}
        <a onClick={() => window.open('/legal', '_blank')}>Terms of Service</a>
        {' '}and{' '}
        <a onClick={() => window.open('/legal', '_blank')}>Privacy Policy</a>.<br />
        Card required today. £99.99/month starts automatically after 90 days unless cancelled before the trial ends. The 12-month minimum term starts after the first successful paid invoice.
      </div>

      <button type="button" className="btn btn-ghost btn-full" style={{ marginTop: 8 }} onClick={async () => { await signOut(); navigate('/login') }}>
        Sign out
      </button>
    </form>
  )
}

export default function SubscribePage() {
  const { user, profile, loading, accessLevel, isSuperAdmin } = useAuth()
  // Read-only workshops stay here so they can reactivate.
  const hasFullAccess = accessLevel === 'full'
  const navigate = useNavigate()

  useEffect(() => {
    const hash = window.location.hash || ''
    if (hash.includes('type=recovery')) {
      navigate(`/reset-password${hash}`, { replace: true })
      return
    }
    if (hash.includes('type=invite')) {
      navigate(`/accept-invite${hash}`, { replace: true })
      return
    }
    if (!loading && user && (hasFullAccess || isSuperAdmin)) {
      navigate('/', { replace: true })
    }
  }, [loading, user, hasFullAccess, isSuperAdmin, navigate])

  if (loading || (user && (hasFullAccess || isSuperAdmin))) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" style={{ width: 24, height: 24 }} />
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />
  if (!profile) return <Navigate to="/setup-workshop" replace />

  return (
    <div className="auth-wrap">
      <div className="auth-card" style={{ maxWidth: 480 }}>
        <div className="auth-logo">Powered by <span>Rebound</span></div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Start your 90-day free trial</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            Get full access to Rebound with card collected now and automatic monthly billing after the trial unless you cancel first.
          </div>
        </div>

        <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 6, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontWeight: 700 }}>Rebound Monthly</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--orange)' }}>90 days free</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>Then £99.99/month. One lifetime trial per workshop.</div>
          {[
            'Unlimited customers & service records',
            'Digital FLI-standard service sheets',
            'Fox & RockShox serial lookup',
            'Professional customer reports',
            'Dashboard & revenue tracking',
            'Service menu with AI import'
          ].map(f => (
            <div key={f} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--light)', padding: '3px 0' }}>
              <span style={{ color: 'var(--green)' }}>✓</span>{f}
            </div>
          ))}
        </div>

        <Elements stripe={stripePromise}>
          <CheckoutForm />
        </Elements>
      </div>
    </div>
  )
}
